import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LogOut,
  Info,
  MoreHorizontal,
  Settings,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { CompanySwitcher } from "./CompanySwitcher";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { arrayMove } from "@dnd-kit/sortable";
import { SearchDialog } from "@/components/SearchDialog";
import { BottomNavCustomizer } from "./BottomNavCustomizer";
import { allNavItems, DEFAULT_PRIMARY_PATHS, LONG_PRESS_MS, STORAGE_KEY } from "./navItems";

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isSystemOwner, isAdmin, isCompanyOwner } = useUserRole();
  const isPhone = useIsMobile();
  const [moreOpen, setMoreOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [editingNav, setEditingNav] = useState(false);
  const [primaryPaths, setPrimaryPaths] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_PRIMARY_PATHS;
    } catch {
      return DEFAULT_PRIMARY_PATHS;
    }
  });

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggered = useRef(false);

  const savePaths = useCallback((paths: string[]) => {
    setPrimaryPaths(paths);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(paths));
  }, []);

  const availableItems = useMemo(() => {
    return allNavItems.filter((item) => {
      if (item.systemOnly && !isSystemOwner) return false;
      if (item.adminOnly && !isSystemOwner && !isAdmin && !isCompanyOwner) return false;
      return true;
    });
  }, [isSystemOwner, isAdmin, isCompanyOwner]);

  const primaryItems = useMemo(() => {
    const filtered = primaryPaths.map((p) => availableItems.find((i) => i.path === p)).filter(Boolean) as typeof availableItems;
    if (filtered.length < 4) {
      for (const item of availableItems) {
        if (filtered.length >= 4) break;
        if (!filtered.find((f) => f.path === item.path)) filtered.push(item);
      }
    }
    return filtered.slice(0, 4);
  }, [primaryPaths, availableItems]);

  const moreItems = useMemo(
    () => availableItems.filter((item) => !primaryItems.find((p) => p.path === item.path)),
    [primaryItems, availableItems]
  );

  const isActive = (path: string) => (path === "/" ? location.pathname === "/" : location.pathname.startsWith(path));
  const handleItemActivate = (item: { path: string; action?: "search" }) => {
    if (item.action === "search") {
      setSearchOpen(true);
      setMoreOpen(false);
      return;
    }
    navigate(item.path);
    setMoreOpen(false);
  };
  const handleNavigation = (path: string) => { navigate(path); setMoreOpen(false); };

  useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const togglePrimaryItem = (path: string) => {
    const currentPaths = primaryItems.map((i) => i.path);
    let newPaths: string[];
    if (currentPaths.includes(path)) {
      if (currentPaths.length <= 1) return;
      newPaths = currentPaths.filter((p) => p !== path);
    } else {
      newPaths = currentPaths.length >= 4 ? [...currentPaths.slice(0, 3), path] : [...currentPaths, path];
    }
    savePaths(newPaths);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    savePaths(arrayMove(primaryPaths, primaryPaths.indexOf(active.id), primaryPaths.indexOf(over.id)));
  };

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  }, []);

  const handlePointerDown = useCallback(() => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => { longPressTriggered.current = true; navigator.vibrate?.(50); setEditingNav(true); }, LONG_PRESS_MS);
  }, []);

  const handlePointerUp = useCallback(() => { clearLongPress(); }, [clearLongPress]);

  return (
    <>
      <BottomNavCustomizer
        open={editingNav}
        onOpenChange={setEditingNav}
        primaryItems={primaryItems}
        moreItems={moreItems}
        primaryPaths={primaryPaths}
        onToggle={togglePrimaryItem}
        onDragEnd={handleDragEnd}
      />

      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-card/90 backdrop-blur-xl border-t border-border shadow-[0_-2px_15px_rgba(42,36,28,0.06)] pb-[env(safe-area-inset-bottom,0px)]">
        <div className="mx-auto flex items-center justify-evenly h-[72px] px-2 max-w-md">
          {primaryItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => handleItemActivate(item)}
                className={cn(
                  "relative flex flex-col items-center justify-center flex-1 min-h-[48px] min-w-[48px] gap-1 transition-colors active:scale-[0.92] rounded-xl py-1",
                  active ? "text-accent-ink" : "text-foreground-muted hover:text-foreground"
                )}
              >
                {active && (
                  <span className="absolute inset-x-3 top-0 h-[3px] rounded-b-full bg-accent" />
                )}
                <motion.span
                  className={cn(
                    "relative flex items-center justify-center h-9 w-9 rounded-xl transition-colors",
                    active && "bg-accent-soft"
                  )}
                  whileTap={{ scale: 0.85 }}
                  transition={{ type: "spring", stiffness: 400, damping: 17 }}
                >
                  <Icon className={cn("h-5 w-5", active && "text-accent")} strokeWidth={active ? 2.2 : 1.8} />
                </motion.span>
                <span className="text-[10px] font-semibold truncate max-w-[64px] text-center leading-tight">{item.label}</span>
              </button>
            );
          })}

          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                onPointerDown={handlePointerDown}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
                onClick={() => { if (longPressTriggered.current) { longPressTriggered.current = false; return; } }}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 min-h-[48px] min-w-[48px] gap-1 transition-colors select-none active:scale-[0.92] rounded-xl py-1",
                  moreOpen ? "text-accent-ink" : "text-foreground-muted hover:text-foreground"
                )}
              >
                <span className={cn("flex items-center justify-center h-9 w-9 rounded-xl transition-colors", moreOpen && "bg-accent-soft")}>
                  <MoreHorizontal className={cn("h-5 w-5", moreOpen && "text-accent")} strokeWidth={moreOpen ? 2.2 : 1.8} />
                </span>
                <span className="text-[10px] font-semibold">Mer</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[60vh] rounded-t-xl flex flex-col">
              <SheetHeader className="pb-4 flex-shrink-0">
                <div className="flex items-center justify-center gap-2">
                  <SheetTitle className="text-center">Meny</SheetTitle>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        aria-label="Information om menyn"
                        className="h-5 w-5 rounded-full bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center transition-colors"
                      >
                        <Info className="h-3 w-3" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent side="bottom" align="center" className="text-xs max-w-[260px]">
                      💡 Håll inne på Mer-knappen i 3 sekunder för att anpassa vilka ikoner som visas i bottenmenyn.
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="flex justify-center pt-3">
                  <CompanySwitcher variant="mobile" />
                </div>
              </SheetHeader>
              <ScrollArea className="flex-1 min-h-0">
                <div className="space-y-6 pb-8 pr-4">
                  {moreItems.length > 0 && (
                    <div>
                      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 text-center">Sidor</h3>
                      <div className={`grid gap-3 ${isPhone ? "grid-cols-3" : "grid-cols-4"}`}>
                        {moreItems.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.path);
                          return (
                            <button key={item.path} onClick={() => handleItemActivate(item)} className={cn("flex flex-col items-center justify-center gap-2 p-3 rounded-xl transition-colors active:scale-[0.92] min-h-[44px] border border-border", active ? "bg-accent-soft text-accent-ink" : "bg-background-2 text-foreground-muted hover:bg-accent-soft hover:text-accent-ink")}>
                              <Icon className="h-5 w-5" strokeWidth={1.8} />
                              <span className="text-[10px] font-semibold text-center leading-tight">{item.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div>
                    <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 text-center">Åtgärder</h3>
                    <div className="grid gap-3 grid-cols-2">
                      <button onClick={() => handleNavigation("/settings")} className={cn("flex flex-col items-center justify-center gap-2 p-3 rounded-xl transition-colors active:scale-[0.92] min-h-[44px] border border-border", isActive("/settings") ? "bg-accent-soft text-accent-ink" : "bg-background-2 text-foreground-muted hover:bg-accent-soft hover:text-accent-ink")}>
                        <Settings className="h-5 w-5" strokeWidth={1.8} /><span className="text-[10px] font-semibold text-center leading-tight">Inställningar</span>
                      </button>
                      <button onClick={() => { setMoreOpen(false); signOut(); }} className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl bg-background-2 text-destructive hover:bg-terra-soft transition-colors active:scale-[0.92] min-h-[44px] border border-border">
                        <LogOut className="h-5 w-5" strokeWidth={1.8} /><span className="text-[10px] font-semibold text-center leading-tight">Logga ut</span>
                      </button>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
